import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

function OnboardModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    orgName: '', adminLogin: '', firstName: '', lastName: '', email: '', password: '',
  });
  const [step, setStep]     = useState(1);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.orgName.trim()) { setErr(t('MyClients.orgNameRequired')); return; }
    if (step === 2 && (!form.adminLogin || !form.email)) { setErr(t('MyClients.loginEmailRequired')); return; }
    setSaving(true);
    setErr('');
    try {
      const body = step === 1 ? { orgName: form.orgName }
        : { ...form };
      const res = await axiosInstance.post('/partner/context/clients', body, { headers: authHeaders() });
      onCreated(res.data);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || t('MyClients.onboardingFailed'));
    } finally {
      setSaving(false);
    }
  };

  const inp = (k, ph, type = 'text') => (
    <input type={type} value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph}
      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db',
               fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }} />
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 440, boxShadow: '0 24px 48px rgba(0,0,0,.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{t('MyClients.onboardNewClient')}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 20 }}>{t('MyClients.stepOf', { step })}</div>

        {err && <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{err}</div>}

        {step === 1 && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('MyClients.organisationName')}</div>
            {inp('orgName', t('MyClients.orgNamePlaceholder'))}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button onClick={() => { if (!form.orgName.trim()) { setErr(t('MyClients.orgNameRequired')); return; } setErr(''); setStep(2); }}
                style={{ flex: 1, padding: 10, background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                {t('MyClients.nextAddAdminUser')}
              </button>
              <button onClick={submit} disabled={saving}
                style={{ flex: 1, padding: 10, background: '#fff', color: '#6366f1', border: '2px solid #6366f1', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                {saving ? t('MyClients.creating') : t('MyClients.createWithoutAdmin')}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ background: '#f5f3ff', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#6b21a8', marginBottom: 14 }}>
              {t('MyClients.creatingFor')} <strong>{form.orgName}</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('MyClients.firstName')}</div>{inp('firstName', t('MyClients.firstNamePlaceholder'))}</div>
              <div><div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('MyClients.lastName')}</div>{inp('lastName', t('MyClients.lastNamePlaceholder'))}</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('MyClients.email')}</div>
            {inp('email', t('MyClients.emailPlaceholder'), 'email')}
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('MyClients.login')}</div>
            {inp('adminLogin', t('MyClients.loginPlaceholder'))}
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('MyClients.temporaryPassword')}</div>
            {inp('password', t('MyClients.passwordPlaceholder'), 'password')}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button onClick={() => setStep(1)}
                style={{ padding: '9px 18px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                {t('MyClients.back')}
              </button>
              <button onClick={submit} disabled={saving}
                style={{ flex: 1, padding: 10, background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                {saving ? t('MyClients.creating') : t('MyClients.createClient')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function MyClients() {
  const { t } = useTranslation();
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [showOnboard, setShowOnboard] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/partner/context/clients', { headers: authHeaders() });
      setClients(res.data || []);
    } catch {
      setError(t('MyClients.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const enterView = (client) => {
    localStorage.setItem('partnerContextOrgId',   String(client.id));
    localStorage.setItem('partnerContextOrgName', client.name);
    navigate('/dashboard/client-view');
  };

  const s = {
    page:  { padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 900 },
    h1:    { fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 },
    sub:   { fontSize: 13, color: '#6b7280', marginBottom: 20 },
    card:  { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 16 },
    btn:   (c = '#6366f1', outline) => ({
              padding: '6px 14px', background: outline ? '#fff' : c, color: outline ? c : '#fff',
              border: outline ? `2px solid ${c}` : 'none', borderRadius: 6, cursor: 'pointer',
              fontSize: 12, fontWeight: 600 }),
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;
  if (error)   return <div style={{ padding: 20, color: '#dc2626' }}>{error}</div>;

  return (
    <div style={s.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={s.h1}>{t('MyClients.title')}</div>
          <div style={s.sub}>{t('MyClients.subtitle')}</div>
        </div>
        <button onClick={() => setShowOnboard(true)} style={s.btn()}>
          <i className="fas fa-plus" style={{ marginRight: 6 }} /> {t('MyClients.onboardNewClient')}
        </button>
      </div>

      {clients.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
          <i className="fas fa-building" style={{ fontSize: 36, display: 'block', marginBottom: 12, color: '#d1d5db' }} />
          {t('MyClients.emptyState')}
        </div>
      ) : (
        clients.map(c => (
          <div key={c.id} style={s.card}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="fas fa-building" style={{ color: '#6366f1', fontSize: 18 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                <span style={{ marginRight: 16 }}><i className="fas fa-sitemap" style={{ marginRight: 4 }} />{t('MyClients.frameworkCount', { count: c.frameworkCount })}</span>
                <span><i className="fas fa-users" style={{ marginRight: 4 }} />{t('MyClients.userCount', { count: c.userCount })}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { enterView(c); }} style={s.btn()}>
                <i className="fas fa-eye" style={{ marginRight: 6 }} /> {t('MyClients.enterClientView')}
              </button>
            </div>
          </div>
        ))
      )}

      {showOnboard && (
        <OnboardModal
          onClose={() => setShowOnboard(false)}
          onCreated={c => { setClients(p => [...p, c]); }}
        />
      )}
    </div>
  );
}
